import { tableClassNames, TableInner } from '@chocolate-soup-inc/cs-frontend-components';
import { useEffect, useMemo, useState } from 'react';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage } from '../../components/tablePage/TablePage';
import { useFilter } from '../../contexts/filters';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { useFinancialReport } from '../../entities/reports/queries';
import { TFinancialReportRow } from '../../generated/graphql';
import { ReportActions } from './ReportActions';
import { companyFilter, monthFilter } from './reportFilters';

import './ReportPage.scss';

type TRow = TFinancialReportRow;

const money = new Intl.NumberFormat('en-CA', { currency: 'CAD', style: 'currency' });

export const FinancialReport = () => {
  const { filtersPageMode } = useFilter();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [companyId, setCompanyId] = useState<string>();
  const { data: rows, error: reportError, loading: reportLoading } = useFinancialReport(month, companyId);
  const { data: companies, error: companiesError, loading: companiesLoading } = useQueryListAllCompaniesOptions();

  useEffect(() => {
    filtersPageMode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeCompanies = useMemo(() => companies.filter((company) => company.active), [companies]);
  const sheet = useMemo(
    () => [
      ['Month', month],
      ['Company ID', 'Company', 'Same Month Last Year', 'Actual Shipments', 'Forecast', 'Total Gifts', 'Shipment Cost', 'Client Cost'],
      ...rows.map((row) => [row.companyId, row.companyName, row.previousYear, row.actual, row.forecast, row.gifts, row.shipmentCost, row.clientCost])
    ],
    [month, rows]
  );
  const loading = reportLoading || companiesLoading;
  const error = reportError || companiesError;

  const filters = useMemo<TFiltersProps['filters']>(() => [monthFilter(month, setMonth), companyFilter(activeCompanies, companyId, setCompanyId)], [month, activeCompanies, companyId]);

  return (
    <TablePage dataLoading={loading} error={error} title='Financial Report'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TRow>
          enableSorting={true}
          defaultSorting={{ id: 'companyName', desc: false }}
          columns={[
            {
              accessorKey: 'companyId',
              cell: ({ row }) => (
                <div className='uuid-label' title={row.original.companyId}>
                  {row.original.companyId}
                </div>
              ),
              header: 'Company ID'
            },
            {
              accessorKey: 'companyName',
              header: 'Company'
            },
            {
              accessorKey: 'previousYear',
              header: 'Same Month Last Year'
            },
            {
              accessorKey: 'actual',
              cell: ({ row }) => row.original.actual ?? '-',
              header: 'Actual Shipments'
            },
            {
              accessorKey: 'forecast',
              cell: ({ row }) => row.original.forecast ?? '-',
              header: 'Forecast'
            },
            {
              accessorKey: 'gifts',
              cell: ({ row }) => row.original.gifts ?? '-',
              header: 'Total Gifts'
            },
            {
              accessorKey: 'shipmentCost',
              cell: ({ row }) => (row.original.shipmentCost == null ? '-' : money.format(row.original.shipmentCost)),
              header: 'Shipment Cost'
            },
            {
              accessorKey: 'clientCost',
              cell: ({ row }) => <div className={tableClassNames.quaternary}>{row.original.clientCost == null ? '-' : money.format(row.original.clientCost)}</div>,
              header: 'Client Cost'
            }
          ]}
          data={rows}
          expandable={false}
          fillSelectedRows={false}
          fixedHeader={true}
          hideSubRowsSelect={true}
          hoverableRows={true}
          selectableRows={false}
          virtual={false}
        />
      </div>
      <ReportActions data={sheet} disabled={loading || !!error || rows.length === 0} filename={`cs-financial-report_${month}`} />
    </TablePage>
  );
};
